import React, { useState, useEffect, useCallback, useRef } from 'react';
import { inventoryService, InventoryTransaction, CreateInventoryItemData, InventoryBatch } from '@services/inventoryService';
import InventoryDialog from './InventoryDialog';
import { Box, Card, CardContent, Typography, Button, Chip, IconButton, Tooltip, Avatar, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Badge, Alert, LinearProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Inventory as InventoryIcon,
  Add as AddIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as ShippingIcon,
  Science as ScienceIcon,
  MedicalServices as MedicalIcon,
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface Product {
  id: string;
  name: string;
  sku: string;
  unit: string;
  vat: number;
  type: 'drug' | 'consumable';
  minStock: number;
  active: boolean;
  description?: string;
  currentStock: number;
  totalValue: number;
  lastRestock?: string;
  nextExpiry?: string;
  batches: Array<{
    id: string;
    lot: string;
    expiry: string;
    qty: number;
    locationId: string;
    unitCost: number;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InventoryManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [batches, setBatches] = useState<InventoryBatch[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [expiredBatchesReport, setExpiredBatchesReport] = useState<any[]>([]);
  const [expiringBatchesReport, setExpiringBatchesReport] = useState<any[]>([]);
  const [stockDiscrepanciesReport, setStockDiscrepanciesReport] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchFormDialogOpen, setBatchFormDialogOpen] = useState(false);
  const [selectedProductForBatches, setSelectedProductForBatches] = useState<Product | null>(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [batchQuantity, setBatchQuantity] = useState<number>(0);
  const [batchUnitCost, setBatchUnitCost] = useState<number>(0);
  const [batchExpirationDate, setBatchExpirationDate] = useState<string>('');
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedProductForAdjust, setSelectedProductForAdjust] = useState<Product | null>(null);
  const [adjustDeltaQuantity, setAdjustDeltaQuantity] = useState<number>(0);
  const [adjustReason, setAdjustReason] = useState<string>('');

  const loadInventory = useCallback(async () => {
    if (!user?.clinicId) return;
    setLoading(true);
    setError(null);
    try {
      const { items } = await inventoryService.getInventoryList({
        clinicId: user.clinicId,
        search: searchTermRef.current || undefined,
        limit: 100,
      });
      const clinicBatches = await inventoryService.getBatches({ clinicId: user.clinicId });
      setBatches(clinicBatches);

      const batchesByProduct = clinicBatches.reduce<Record<string, InventoryBatch[]>>((acc, b) => {
        if (!acc[b.product_id]) acc[b.product_id] = [];
        acc[b.product_id].push(b);
        return acc;
      }, {});

      const mapped: Product[] = (items ?? []).map((it: any) => {
        const productBatches = batchesByProduct[it.id] || [];
        const nonExpired = productBatches.filter(
          (b) => !b.is_expired && new Date(b.expiration_date) >= new Date(),
        );
        const nextExpiry =
          nonExpired.length > 0
            ? nonExpired
                .map((b) => b.expiration_date)
                .sort()[0]
            : undefined;

        return {
          id: it.id,
          name: it.name ?? '',
          sku: it.id?.slice(0, 8) ?? '',
          unit: 'buc',
          vat: 19,
          type: (it.category ?? '').toLowerCase().includes('med') ? 'drug' as const : 'consumable' as const,
          minStock: parseFloat(it.min_quantity ?? 0),
          active: true,
          description: it.description ?? '',
          currentStock: parseFloat(it.quantity ?? 0),
          totalValue: (parseFloat(it.quantity ?? 0) * parseFloat(it.unit_price ?? 0)),
          lastRestock: it.updated_at,
          nextExpiry,
          batches: productBatches.map((b) => ({
            id: b.id,
            lot: b.batch_number,
            expiry: b.expiration_date,
            qty: b.quantity_available,
            locationId: it.id,
            unitCost: b.unit_cost,
          })),
          createdAt: it.created_at ?? '',
          updatedAt: it.updated_at ?? '',
        };
      });
      setProducts(mapped);
    } catch (err: any) {
      console.error('Error loading inventory:', err);
      setError(err.message ?? 'Eroare la incarcarea inventarului');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [user?.clinicId]);

  const loadTransactions = useCallback(async () => {
    if (!user?.clinicId) return;
    try {
      const { transactions: txs, total } = await inventoryService.getTransactions({
        clinicId: user.clinicId,
        limit: 100,
      });
      setTransactions(txs ?? []);
      setTransactionsTotal(total ?? 0);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
    }
  }, [user?.clinicId]);

  const loadReports = useCallback(async () => {
    if (!user?.clinicId) return;
    try {
      const [expired, expiring, discrepancies] = await Promise.all([
        inventoryService.getExpiredBatches({ clinicId: user.clinicId }),
        inventoryService.getExpiringBatches({ clinicId: user.clinicId, days: 30 }),
        inventoryService.getStockDiscrepancies({ clinicId: user.clinicId }),
      ]);
      setExpiredBatchesReport(expired);
      setExpiringBatchesReport(expiring);
      setStockDiscrepanciesReport(discrepancies);
    } catch (err) {
      console.error('Error loading inventory reports:', err);
    }
  }, [user?.clinicId]);

  useEffect(() => {
    void loadInventory();
  }, [loadInventory]);

  useEffect(() => {
    if (tabValue === 3 || tabValue === 4) void loadTransactions();
    if (tabValue === 5) void loadReports();
  }, [tabValue, user?.clinicId, loadTransactions, loadReports]);

  useEffect(() => {
    if (searchTerm && user?.clinicId) {
      const debounce = setTimeout(() => void loadInventory(), 300);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm, user?.clinicId, loadInventory]);


  const getTypeColor = (type: string) => {
    switch (type) {
      case 'drug':
        return 'error';
      case 'consumable':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'drug':
        return 'Medicament';
      case 'consumable':
        return 'Consumabil';
      default:
        return type;
    }
  };

  const getStockStatus = (currentStock: number, minStock: number) => {
    if (currentStock <= 0) return { status: 'out', color: 'error', label: 'Stoc epuizat' };
    if (currentStock <= minStock) return { status: 'low', color: 'warning', label: 'Stoc minim' };
    return { status: 'ok', color: 'success', label: 'Stoc OK' };
  };

  const getMoveTypeColor = (type: string) => {
    switch (type) {
      case 'IN':
        return 'success';
      case 'OUT':
        return 'error';
      case 'ADJ':
        return 'warning';
      case 'TRANSFER':
        return 'info';
      default:
        return 'default';
    }
  };

  const getMoveTypeLabel = (type: string) => {
    switch (type) {
      case 'IN':
        return 'Intrare';
      case 'OUT':
        return 'Iesire';
      case 'ADJ':
        return 'Ajustare';
      case 'TRANSFER':
        return 'Transfer';
      default:
        return type;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateProduct = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await inventoryService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Eroare la descarcarea template-ului de import:', e);
      setError('Eroare la descarcarea template-ului de import');
    }
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const result = await inventoryService.importFromFile(file);
      await loadInventory();
      if (result.errors.length > 0) {
        console.warn('Import inventory completed with errors:', result.errors);
        setError(`Import: ${result.imported} randuri importate, ${result.skipped} randuri sarite.`);
      }
    } catch (e) {
      console.error('Eroare la importul de inventar:', e);
      setError('Eroare la importul de inventar');
    } finally {
      setImporting(false);
      // reset input value to allow re-upload of same file
      event.target.value = '';
    }
  };

  const handleSaveProduct = async (data: CreateInventoryItemData) => {
    try {
      if (!user?.clinicId) {
        throw new Error('ClinicId lipsa pentru utilizatorul curent');
      }

      const payload: CreateInventoryItemData = {
        ...data,
        clinicId: user.clinicId,
      };

      await inventoryService.createInventoryItem(payload);
      await loadInventory();
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Eroare la salvarea produsului:', error);
      throw error;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || product.type === selectedType;
    const matchesStatus = selectedStatus === 'all' || 
                         (selectedStatus === 'active' && product.active) ||
                         (selectedStatus === 'inactive' && !product.active);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const lowStockProducts = filteredProducts.filter(p => p.currentStock <= p.minStock);
  const expiredProducts = filteredProducts.filter(p => p.nextExpiry && new Date(p.nextExpiry) < new Date());
  const allProducts = filteredProducts;

  const productBatchesForDialog = selectedProductForBatches
    ? batches.filter((b) => b.product_id === selectedProductForBatches.id)
    : [];

  const handleOpenBatchesDialog = (product: Product) => {
    setSelectedProductForBatches(product);
    setBatchDialogOpen(true);
  };

  const handleOpenBatchForm = (product: Product) => {
    setSelectedProductForBatches(product);
    setBatchNumber('');
    setBatchQuantity(0);
    setBatchUnitCost(0);
    setBatchExpirationDate('');
    setBatchFormDialogOpen(true);
  };

  const handleSaveBatch = async () => {
    if (!selectedProductForBatches) return;
    try {
      await inventoryService.createBatch({
        productId: selectedProductForBatches.id,
        batchNumber,
        expirationDate: batchExpirationDate,
        unitCost: batchUnitCost,
        quantity: batchQuantity,
      });
      setBatchFormDialogOpen(false);
      await loadInventory();
    } catch (e) {
      console.error('Eroare la receptia lotului:', e);
      setError('Eroare la receptia lotului');
    }
  };

  const handleOpenAdjustDialog = (product: Product) => {
    setSelectedProductForAdjust(product);
    setAdjustDeltaQuantity(0);
    setAdjustReason('');
    setAdjustDialogOpen(true);
  };

  const handleSaveAdjustment = async () => {
    if (!selectedProductForAdjust) return;
    if (!Number.isFinite(adjustDeltaQuantity) || adjustDeltaQuantity === 0) {
      setError('Cantitatea de ajustare trebuie sa fie diferita de zero');
      return;
    }
    try {
      await inventoryService.manualAdjust(
        selectedProductForAdjust.id,
        adjustDeltaQuantity,
        adjustReason || 'Ajustare manuala din UI inventar',
      );
      setAdjustDialogOpen(false);
      await Promise.all([loadInventory(), loadTransactions()]);
    } catch (e: any) {
      console.error('Eroare la ajustarea manuala:', e);
      setError(e?.response?.data?.message || 'Eroare la ajustarea manuala a stocului');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Se incarca inventarul...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Inventar
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiune produse (medicamente, consumabile) – receptii, consum, ajustari, alerte minim/expirare, import Excel.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Template Import
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={importing}
          >
            Import Excel
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleImportFileChange}
            />
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateProduct}
            sx={{ borderRadius: 2 }}
          >
            Produs Nou
          </Button>
        </Box>
      </Box>

      {/* Alerte */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              Stoc Minim
            </Typography>
            <Typography variant="body2">
              {lowStockProducts.length} produse cu stoc sub minim
            </Typography>
          </Alert>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Alert severity="error" icon={<WarningIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              Expirari
            </Typography>
            <Typography variant="body2">
              {expiredProducts.length} produse expirate
            </Typography>
          </Alert>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Alert severity="info" icon={<InventoryIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              Valoare Totala
            </Typography>
            <Typography variant="body2">
              {products.reduce((sum, p) => sum + p.totalValue, 0).toFixed(2)} RON
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      {/* Filtre */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Cauta produs sau SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Tip</InputLabel>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  label="Tip"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="drug">Medicamente</MenuItem>
                  <MenuItem value="consumable">Consumabile</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="active">Activ</MenuItem>
                  <MenuItem value="inactive">Inactiv</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                fullWidth
                disabled
                sx={{ height: 56, borderRadius: 2 }}
              >
                Filtre Avansate (indisponibil)
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Tooltip title="Actualizeaza">
                <IconButton
                  onClick={async () => {
                    await loadInventory();
                    if (tabValue === 3 || tabValue === 4) await loadTransactions();
                    if (tabValue === 5) await loadReports();
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid size={{ xs: 12, md: 1 }}>
              <Chip
                label="Doar Medicamente"
                variant="outlined"
                size="small"
                clickable
                onClick={() => setSelectedType(selectedType === 'drug' ? 'all' : 'drug')}
                color={selectedType === 'drug' ? 'primary' : 'default'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="inventory tabs">
            <Tab 
              label={
                <Badge badgeContent={lowStockProducts.length} color="warning">
                  Stoc Minim
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={expiredProducts.length} color="error">
                  Expirari
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={allProducts.length} color="primary">
                  Toate
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={transactions.length} color="info">
                  Miscari
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={transactionsTotal} color="secondary">
                  Tranzactii Audit
                </Badge>
              } 
            />
            <Tab
              label={
                <Badge
                  badgeContent={
                    expiredBatchesReport.length +
                    expiringBatchesReport.length +
                    stockDiscrepanciesReport.length
                  }
                  color="error"
                >
                  Rapoarte
                </Badge>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Produs</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>Stoc Curent</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>Stoc Minim</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>Valoare</TableCell>
                  <TableCell>Loturi</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lowStockProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => {
                    const stockStatus = getStockStatus(product.currentStock, product.minStock);
                    return (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'warning.light' }}>
                              {product.type === 'drug' ? <MedicalIcon /> : <ScienceIcon />}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {product.sku}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {product.currentStock} {product.unit}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.minStock} {product.unit}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {product.totalValue} RON
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleOpenBatchesDialog(product)}
                          >
                            {product.batches.length} loturi
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={stockStatus.label}
                            color={stockStatus.color as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Tooltip title="Receptioneaza lot">
                              <IconButton size="small" onClick={() => handleOpenBatchForm(product)}>
                                <ShippingIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ajusteaza">
                              <IconButton size="small" onClick={() => handleOpenAdjustDialog(product)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Vezi">
                              <IconButton size="small" onClick={() => handleOpenBatchesDialog(product)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={lowStockProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Produs</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell>Data Expirarii</TableCell>
                  <TableCell>Cantitate</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiredProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'error.light' }}>
                            {product.type === 'drug' ? <MedicalIcon /> : <ScienceIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {product.sku}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getTypeLabel(product.type)} 
                          size="small" 
                          color={getTypeColor(product.type) as any} 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.batches[0]?.lot || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error.main">
                          {product.nextExpiry}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.batches[0]?.qty || 0} {product.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Vezi loturi">
                            <IconButton size="small" onClick={() => handleOpenBatchesDialog(product)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={expiredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Produs</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>Stoc Curent</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>Stoc Minim</TableCell>
                  <TableCell sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}>Valoare</TableCell>
                  <TableCell>Loturi</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => {
                    const stockStatus = getStockStatus(product.currentStock, product.minStock);
                    return (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ 
                              bgcolor: stockStatus.status === 'ok' ? 'primary.light' : 
                                      stockStatus.status === 'low' ? 'warning.light' : 'error.light'
                            }}>
                              {product.type === 'drug' ? <MedicalIcon /> : <ScienceIcon />}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {product.sku}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {product.currentStock} {product.unit}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.minStock} {product.unit}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {product.totalValue} RON
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleOpenBatchesDialog(product)}
                          >
                            {product.batches.length} loturi
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={stockStatus.label}
                            color={stockStatus.color as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Vezi loturi">
                              <IconButton size="small" onClick={() => handleOpenBatchesDialog(product)}>
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ajusteaza">
                              <IconButton size="small" onClick={() => handleOpenAdjustDialog(product)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editeaza produs - indisponibil in acest flow">
                              <span>
                                <IconButton size="small" disabled>
                                  <TrendingUpIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={allProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Cantitate</TableCell>
                  <TableCell>Referinta</TableCell>
                  <TableCell>Utilizator</TableCell>
                  <TableCell>Nota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions
                  .map((t) => ({
                    id: t.id,
                    type: (t.quantity > 0 ? 'OUT' : 'IN') as 'IN' | 'OUT' | 'ADJ' | 'TRANSFER',
                    qty: Math.abs(t.quantity),
                    batchId: '',
                    locationId: '',
                    refType: t.transaction_type,
                    refId: t.visit_id ?? t.prescription_id ?? t.procedure_id ?? '-',
                    createdBy: t.created_by,
                    createdAt: t.created_at,
                    notes: '',
                  }))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((move) => (
                    <TableRow key={move.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateOnly(move.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeOnly(move.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getMoveTypeLabel(move.type)}
                          color={getMoveTypeColor(move.type) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {move.qty}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {move.refType}: {move.refId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {move.createdBy}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {move.notes || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={transactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tranzactii inventar: consum din prescriptii/proceduri, revert, ajustare manuala.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Produs</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Cantitate</TableCell>
                  <TableCell>Sold dupa</TableCell>
                  <TableCell>Legatura vizita</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2">{formatDateOnly(t.created_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatTimeOnly(t.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.product_id?.slice(0, 8)}...</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t.transaction_type} size="small" color={t.quantity > 0 ? 'error' : 'success'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {t.quantity > 0 ? '-' : '+'}{Math.abs(t.quantity)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.balance_after}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.visit_id ? t.visit_id.slice(0, 8) + '...' : '-'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={transactionsTotal}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Stack spacing={2}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Loturi expirate ({expiredBatchesReport.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {expiredBatchesReport.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Nu exista loturi expirate.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Lot</TableCell>
                          <TableCell>Produs</TableCell>
                          <TableCell>Expira la</TableCell>
                          <TableCell>Cantitate disponibila</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expiredBatchesReport.slice(0, 25).map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.batch_number}</TableCell>
                            <TableCell>{row.product_id?.slice(0, 8)}...</TableCell>
                            <TableCell>{formatDateOnly(row.expiration_date)}</TableCell>
                            <TableCell>{row.quantity_available}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Loturi care expira in 30 zile ({expiringBatchesReport.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {expiringBatchesReport.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Nu exista loturi care expira in urmatoarele 30 zile.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Lot</TableCell>
                          <TableCell>Produs</TableCell>
                          <TableCell>Expira la</TableCell>
                          <TableCell>Cantitate disponibila</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {expiringBatchesReport.slice(0, 25).map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.batch_number}</TableCell>
                            <TableCell>{row.product_id?.slice(0, 8)}...</TableCell>
                            <TableCell>{formatDateOnly(row.expiration_date)}</TableCell>
                            <TableCell>{row.quantity_available}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Discrepante stoc ({stockDiscrepanciesReport.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {stockDiscrepanciesReport.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Nu exista discrepante detectate.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Produs</TableCell>
                          <TableCell>Stoc inventory</TableCell>
                          <TableCell>Stoc batch-uri</TableCell>
                          <TableCell>Diferenta</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stockDiscrepanciesReport.slice(0, 25).map((row: any) => (
                          <TableRow key={row.product_id}>
                            <TableCell>{row.product_name}</TableCell>
                            <TableCell>{row.inventory_quantity}</TableCell>
                            <TableCell>{row.batches_quantity}</TableCell>
                            <TableCell>{row.diff}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </TabPanel>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Create / Edit Product Dialog */}
      <InventoryDialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        onSave={handleSaveProduct}
      />

      {/* Batches list dialog */}
      <Dialog
        open={batchDialogOpen}
        onClose={() => setBatchDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Loturi pentru {selectedProductForBatches?.name}</DialogTitle>
        <DialogContent dividers>
          {productBatchesForDialog.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nu există loturi pentru acest produs.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Lot</TableCell>
                    <TableCell>Data expirării</TableCell>
                    <TableCell>Cantitate inițială</TableCell>
                    <TableCell>Cantitate disponibilă</TableCell>
                    <TableCell>Cost unitar</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productBatchesForDialog.map((b) => {
                    const expired =
                      b.is_expired || new Date(b.expiration_date) < new Date();
                    const almostExpired =
                      !expired &&
                      new Date(b.expiration_date) <
                        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                    return (
                      <TableRow key={b.id} hover>
                        <TableCell>{b.batch_number}</TableCell>
                        <TableCell>{formatDateOnly(b.expiration_date)}</TableCell>
                        <TableCell>{b.quantity_initial}</TableCell>
                        <TableCell>{b.quantity_available}</TableCell>
                        <TableCell>{b.unit_cost} RON</TableCell>
                        <TableCell>
                          {expired ? (
                            <Chip label="Expirat" color="error" size="small" />
                          ) : almostExpired ? (
                            <Chip
                              label="Aproape expirat"
                              color="warning"
                              size="small"
                            />
                          ) : (
                            <Chip label="Activ" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajustare stoc pentru {selectedProductForAdjust?.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Cantitate pozitiva = intrare stoc, cantitate negativa = iesire stoc.
            </Alert>
            <TextField
              label="Delta cantitate"
              type="number"
              value={adjustDeltaQuantity}
              onChange={(e) => setAdjustDeltaQuantity(parseFloat(e.target.value))}
              fullWidth
              required
              inputProps={{ step: 0.01 }}
            />
            <TextField
              label="Motiv ajustare"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Anuleaza</Button>
          <Button
            onClick={handleSaveAdjustment}
            variant="contained"
            disabled={!Number.isFinite(adjustDeltaQuantity) || adjustDeltaQuantity === 0}
          >
            Salveaza ajustarea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive batch dialog */}
      <Dialog
        open={batchFormDialogOpen}
        onClose={() => setBatchFormDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Receptie lot pentru {selectedProductForBatches?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Număr lot"
              value={batchNumber}
              onChange={(e) => setBatchNumber(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Cantitate"
              type="number"
              value={batchQuantity}
              onChange={(e) => setBatchQuantity(parseFloat(e.target.value))}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Cost unitar (RON)"
              type="number"
              value={batchUnitCost}
              onChange={(e) => setBatchUnitCost(parseFloat(e.target.value))}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Data expirării"
              type="date"
              value={batchExpirationDate}
              onChange={(e) => setBatchExpirationDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchFormDialogOpen(false)}>Anulează</Button>
          <Button
            onClick={handleSaveBatch}
            variant="contained"
            disabled={
              !selectedProductForBatches ||
              !batchNumber ||
              !batchExpirationDate ||
              !Number.isFinite(batchQuantity) ||
              batchQuantity <= 0 ||
              !Number.isFinite(batchUnitCost) ||
              batchUnitCost < 0
            }
          >
            Salvează lot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement;

